import { useQuery } from '@apollo/client';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
  ReviewCommentParentType,
  Role,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { TargetBookmarksManagerModal } from '~/components/bookmark/TargetBookmarksManagerModal';
import { LastUpdated } from '~/components/common/LastUpdated';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { Prose } from '~/components/common/Prose';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { TargetReviewCommentsModal } from '~/components/review-comment/TargetReviewCommentsModal';
import { useRouteParam } from '~/hooks/routing';
import { otherWikiDetailRoute, uploadOtherWikiUpdateRoute } from '~/paths';

const OTHER_WIKI_DETAIL_PAGE = graphql(`
  query OtherWikiDetailPage($id: ID!) {
    otherWikiGet(id: $id) {
      ...otherWikiParts
    }
  }
`);

export default function OtherWikiDetailPage() {
  return (
    <ApolloProviderV4>
      <OtherWikiDetailPageInner />
    </ApolloProviderV4>
  );
}

function OtherWikiDetailPageInner() {
  const otherId = useRouteParam('otherId');

  const { data, loading } = useQuery(OTHER_WIKI_DETAIL_PAGE, {
    variables: { id: otherId },
  });

  const wikiPage = data?.otherWikiGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!wikiPage) return <NotFound />;

  return (
    <>
      <div className="float-right space-x-2">
        <TargetBookmarksManagerModal
          parentType={BookmarkParentType.OtherWiki}
          parentId={parseInt(wikiPage.id)}
          targetType={BookmarkTargetType.OtherWiki}
          targetId={parseInt(wikiPage.id)}
          path={otherWikiDetailRoute(parseInt(wikiPage.id))}
        />

        <TargetReviewCommentsModal
          parentId={parseInt(wikiPage.id)}
          parentType={ReviewCommentParentType.WikiOther}
          name={wikiPage.title}
        />
        <RoleSecured roles={[Role.RoleAdmin]}>
          <Link
            to={uploadOtherWikiUpdateRoute(parseInt(wikiPage.id))}
            className="btn btn-ghost btn-sm gap-1"
          >
            <FontAwesomeIcon icon={faPencil} /> Edit Page
          </Link>
        </RoleSecured>
      </div>

      <PageHeading>{wikiPage.title}</PageHeading>

      {wikiPage.text && (
        <Prose dangerouslySetInnerHTML={{ __html: wikiPage.text }} />
      )}

      <LastUpdated date={wikiPage.updatedAt} />
    </>
  );
}
